import apiClient from './apiClient';
import type {
  IntakeRequestRow,
  IntakeDetail,
  ConvertIntakeToQuoteResult,
  ConvertIntakeToWorkOrderResult,
} from '../types/intake';

/** Lab-side intake management (auth). */

export interface CreateIntakeLinkRequest {
  clientKey?: string | null;
  clientName?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  projectName?: string | null;
  expiryDays?: number | null;
}

export const createIntakeLink = async (
  data: CreateIntakeLinkRequest,
): Promise<{ intake: IntakeRequestRow; token: string }> => {
  const response = await apiClient.post<{ intake: IntakeRequestRow; token: string }>('/intake', data);
  return response.data;
};

export const listIntakes = async (): Promise<IntakeRequestRow[]> => {
  const response = await apiClient.get<IntakeRequestRow[]>('/intake');
  return response.data;
};

export const getIntakeCounts = async (): Promise<{ submitted: number }> => {
  const response = await apiClient.get<{ submitted: number }>('/intake/counts');
  return response.data;
};

export const getIntakeDetail = async (key: string): Promise<IntakeDetail> => {
  const response = await apiClient.get<IntakeDetail>(`/intake/${key}`);
  return response.data;
};

export interface ReturnIntakeResult {
  ok: boolean;
  emailSent: boolean;
  deliveryConfigured: boolean;
  contactEmail: string | null;
}

export const returnIntakeToClient = async (key: string, note: string): Promise<ReturnIntakeResult> => {
  const response = await apiClient.post<ReturnIntakeResult>(`/intake/${key}/return`, { note });
  return response.data;
};

export const cancelIntake = async (key: string): Promise<void> => {
  await apiClient.post(`/intake/${key}/cancel`);
};

export const convertIntakeToQuote = async (key: string): Promise<ConvertIntakeToQuoteResult> => {
  const response = await apiClient.post<ConvertIntakeToQuoteResult>(`/intake/${key}/convert-to-quote`);
  return response.data;
};

// ── Admin: intake form configuration ────────────────────────────────────────

export interface IntakeFormConfig {
  visibleCategories: string[];
  hiddenTestKeys: string[];
  instructionsBlurb: string;
  checklistItems: { id: string; text: string }[];
}

export interface IntakeFormConfigView {
  config: IntakeFormConfig;
  availableCategories: { name: string; testCount: number }[];
  tests: { key: string; name: string; astm: string | null; category: string }[];
}

export const getIntakeFormConfig = async (): Promise<IntakeFormConfigView> => {
  const response = await apiClient.get<IntakeFormConfigView>('/intake/form-config');
  return response.data;
};

export const updateIntakeFormConfig = async (
  patch: Partial<IntakeFormConfig>,
): Promise<IntakeFormConfigView> => {
  const response = await apiClient.put<IntakeFormConfigView>('/intake/form-config', patch);
  return response.data;
};

export interface DeleteIntakeOptions {
  deleteQuote?: boolean;
  deleteWorkOrder?: boolean;
  deleteSamples?: boolean;
  releaseSchedule?: boolean;
}

export interface DeleteIntakeResult {
  intakeDeleted: true;
  quoteDeleted: boolean;
  workOrderDeleted: boolean;
  samplesDeleted: number;
  locationsDeleted: number;
}

export const deleteIntake = async (
  key: string,
  opts: DeleteIntakeOptions,
): Promise<DeleteIntakeResult> => {
  const params = new URLSearchParams();
  if (opts.deleteQuote) params.set('deleteQuote', 'true');
  if (opts.deleteWorkOrder) params.set('deleteWorkOrder', 'true');
  if (opts.deleteSamples) params.set('deleteSamples', 'true');
  if (opts.releaseSchedule) params.set('releaseSchedule', 'true');
  const response = await apiClient.delete<DeleteIntakeResult>(
    `/intake/${key}?${params.toString()}`,
  );
  return response.data;
};

export const convertIntakeToWorkOrder = async (
  key: string,
  projectKey?: string | null,
): Promise<ConvertIntakeToWorkOrderResult> => {
  const response = await apiClient.post<ConvertIntakeToWorkOrderResult>(
    `/intake/${key}/convert-to-workorder`,
    { projectKey: projectKey ?? null },
  );
  return response.data;
};
